import { createSlice } from '@reduxjs/toolkit';

/**
 * Who is currently typing, per thread.
 *
 * This is deliberately NOT part of chatSlice. `getChatControlData` is
 * `(state) => state.chatSlice`, and MessageListItem subscribes to it once per
 * rendered message — so mutating chatSlice re-renders every bubble in the
 * thread. A typing ping arrives every few seconds while someone writes, which
 * through that selector would be a continuous re-render storm. Keeping the map
 * in its own slice leaves state.chatSlice's reference untouched: a TYPING frame
 * falls through chatSlice's websocket branch chain without mutating anything.
 *
 * Nothing here is persisted, and nothing here is written to the backend. The
 * frames are broadcast by the chat websocket lambda and never stored.
 */

// How often a typist re-announces themselves while they keep writing.
export const TYPING_PING_INTERVAL_MS = 3000;

// How long a received ping stays valid. Twice the ping interval, so a single
// dropped frame makes the indicator linger rather than blink.
export const TYPING_TTL_MS = 6000;

const initialState = {
  // { [threadId]: { [userId]: expiresAt } } — expiresAt is the *receiver's*
  // clock, so the sender's clock skew never enters the comparison.
  typingByThread: {},
};

export const chatTypingSlice = createSlice({
  name: 'chatTyping',
  initialState,
  reducers: {
    typingReceived(state, action) {
      const { threadId, userId, isTyping, expiresAt } = action.payload;
      if (!threadId || !userId) return;

      if (isTyping) {
        if (!state.typingByThread[threadId]) {
          state.typingByThread[threadId] = {};
        }
        state.typingByThread[threadId][userId] = expiresAt;
        return;
      }

      // Guard before deleting: an unconditional delete of a key that is not
      // there still hands Immer a new object, and this slice's whole point is
      // that its identity only changes when something really changed.
      const typists = state.typingByThread[threadId];
      if (!typists || typists[userId] === undefined) return;

      delete typists[userId];
      if (!Object.keys(typists).length) {
        delete state.typingByThread[threadId];
      }
    },

    /** Prune everyone whose last ping has aged out. Payload is `Date.now()`. */
    typingExpired(state, action) {
      const now = action.payload;

      // Object.entries snapshots first, so deleting while iterating is safe.
      for (const [threadId, typists] of Object.entries(state.typingByThread)) {
        let pruned = false;
        for (const [userId, expiresAt] of Object.entries(typists)) {
          if (expiresAt <= now) {
            delete typists[userId];
            pruned = true;
          }
        }
        if (pruned && !Object.keys(typists).length) {
          delete state.typingByThread[threadId];
        }
      }
    },
  },
});

export const { typingReceived, typingExpired } = chatTypingSlice.actions;

export default chatTypingSlice.reducer;
